import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./AeroSearch.css";

// Debounce helper
const useDebounce = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
};

const AeroSearch = () => {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("PG");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [placeholder, setPlaceholder] = useState("Search PG near Kaziranga University");
  const [fade, setFade] = useState(true);
  const [fadeIndex, setFadeIndex] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const searchRef = useRef(null);
  const navigate = useNavigate();

  const offlineSuggestions = useMemo(() => [
    "Jorhat", "Sivasagar", "Golaghat", "Dibrugarh", "Tezpur", "Dergaon",
    "Paltan Bazar", "Ganeshguri", "Beltola", "Six Mile", "Basistha",
    "Chandmari", "Pan Bazaar", "Jalukbari", "Dispur", "Khanapara",
    "Fancy Bazar", "Bhangagarh",
    "Malow Ali", "Gar Ali", "Cinnamara", "Jorhat Club Road",
    "Bhogdoi Road", "Jorhat Road", "Amolapatty",
    "Atilagaon", "Raraiyah Road", "Tilikiam", "Choladhora",
    "Kumarpara", "Tinikuria", "Brahmapur", "Sonari gaon",
    "JPR Road", "Nehru Park Road", "K.B. Road",
    "Namoni Sivasagar", "Rangajan", "Gogamukh Road",
    "Gauhati University", "Cotton University", "Assam Engineering College",
    "IIT Guwahati", "Assam Don Bosco University", "Assam Agricultural University",
    "Jorhat Engineering College", "Kaziranga University",
    "Dibrugarh University", "Tezpur University",
  ], []);

  const preCacheLocations = useMemo(() => ({
    default: { lat: 26.7509, lng: 94.2226 },
    jorhat: { lat: 26.7509, lng: 94.2226 },
    "jorhat club road": { lat: 26.7540, lng: 94.2055 },
    "bhogdoi road": { lat: 26.7587, lng: 94.2158 },
    "jorhat road": { lat: 26.7470, lng: 94.2210 },
    amolapatty: { lat: 26.7569, lng: 94.2041 },
  }), []);

  // Merge pre-cache locations into localStorage once
  useEffect(() => {
    const existing = JSON.parse(localStorage.getItem("geoCache") || "{}");
    localStorage.setItem("geoCache", JSON.stringify({ ...preCacheLocations, ...existing }));
  }, [preCacheLocations]);

  // Placeholder fade texts
  const fadeTexts = ["Find your perfect space 🏠", "Enter your location, we’ll do the rest 💖"];
  useEffect(() => {
    const interval = setInterval(() => setFadeIndex(prev => (prev + 1) % fadeTexts.length), 4000);
    return () => clearInterval(interval);
  }, []);

  // Placeholder rotation
  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setPlaceholder(`Search ${mode} near ${offlineSuggestions[index]}`);
        setFade(true);
        index = (index + 1) % offlineSuggestions.length;
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, [mode, offlineSuggestions]);

  // Click outside to close suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    const handleClickOutside = e => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSuggestions([]);
        setIsTyping(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll collapse
  useEffect(() => {
    const handleScroll = () => {
      if (!searchRef.current) return;
      const rect = searchRef.current.getBoundingClientRect();
      setIsCollapsed(rect.bottom < 0 || rect.top < -50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Debounced query
  const debouncedQuery = useDebounce(query, 200);

  // Filtered suggestions
  const filteredSuggestions = useMemo(() => {
    const cleanInput = debouncedQuery.replace(new RegExp(`^${mode} near `, "i"), "").trim().toLowerCase();
    if (!cleanInput) return [];
    return offlineSuggestions.filter(item => item.toLowerCase().includes(cleanInput));
  }, [debouncedQuery, mode, offlineSuggestions]);

  useEffect(() => {
    setSuggestions(filteredSuggestions.map(item => ({ description: item })));
  }, [filteredSuggestions]);

  // Handle input change
  const handleInputChange = e => {
    setQuery(e.target.value);
    setIsTyping(true);
  };

  // Handle suggestion click
  const handleSuggestionClick = useCallback(desc => {
    const cache = JSON.parse(localStorage.getItem("geoCache") || "{}");
    const { lat, lng } = cache[desc.toLowerCase()] || cache.default;

    navigate(`/AeroSearchUI?mode=${mode}&lat=${lat}&lng=${lng}&place=${encodeURIComponent(desc)}`);
    setSuggestions([]);
    setQuery(`${mode} near ${desc}`);
  }, [mode, navigate]);

  // Handle submit
  const handleSubmit = e => {
    e.preventDefault();
    if (!query.trim()) return;
    const loc = query.replace(/^(PG|Rent)\s+near\s*/i, "").trim();
    const exists = offlineSuggestions.some(item => item.toLowerCase() === loc.toLowerCase());
    if (!exists) {
      alert("No matching location found!");
      return;
    }
    handleSuggestionClick(loc);
  };

  // Keyboard navigation
  const handleKeyDown = e => {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      setHighlightedIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      handleSuggestionClick(suggestions[highlightedIndex].description);
      setHighlightedIndex(-1);
    }
  };

  const handleIconClick = () => searchRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <>
      <div ref={searchRef} className="aero-search-wrapper">
        <form onSubmit={handleSubmit} className="aero-search" autoComplete="off">
          <div className="fade-heading-container">
            <h2 key={fadeIndex} className={`fade-text ${fade ? "fade-in" : "fade-out"}`}>
              {fadeTexts[fadeIndex]}
            </h2>
          </div>

          <div className="state-label">Assam</div>

          <div className="mode-toggle">
            <span className={mode === "PG" ? "active" : ""} onClick={() => { setMode("PG"); setQuery(""); }}>PG</span>
            <span className={mode === "Rent" ? "active" : ""} onClick={() => { setMode("Rent"); setQuery(""); }}>Rent</span>
          </div>

          <div className="map-searchbar position-relative">
            <div className="input-group shadow-sm searchbar-container">
              <span className="input-group-text bg-transparent border-0 ps-3">
                <FaSearch className="text-muted fs-5" />
              </span>

              <input
                type="text"
                autoComplete="off"
                className={`form-control border-0 shadow-none ps-1 animated-placeholder`}
                placeholder={placeholder}
                value={query}
                onChange={handleInputChange}
                onFocus={() => { setIsTyping(true); if (!query.trim()) setQuery(`${mode} near `); }}
                onKeyDown={handleKeyDown}
                style={{ fontSize: "16px", fontWeight: "500", transition: "all 0.3s ease" }}
              />

              <button type="submit" className="btn btn-dark rounded-circle d-flex align-items-center justify-content-center me-2">
                <FaSearch className="text-white fs-5" />
              </button>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <ul className="suggestions-list list-group position-absolute w-100 shadow-lg mt-1">
                {suggestions.map((item, i) => {
                  const term = query.replace(/^(PG|Rent)\s+near\s*/i, "").trim();
                  const regex = new RegExp(`(${term})`, "gi");
                  const highlighted = item.description.replace(regex, "<strong>$1</strong>");
                  return (
                    <li
                      key={i}
                      className={`list-group-item list-group-item-action py-2 px-3 ${highlightedIndex === i ? "active" : ""}`}
                      onClick={() => handleSuggestionClick(item.description)}
                      dangerouslySetInnerHTML={{ __html: highlighted }}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        </form>
      </div>

      {isCollapsed && (
        <div className="floating-search-icon" onClick={handleIconClick}>
          <FaSearch />
        </div>
      )}
    </>
  );
};

export default AeroSearch;
